import {
  DefaultService,
  Organisation,
  OrganisationType,
} from "@/api/SolarCloudApi";
import { v4 as uuid } from "uuid";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { MutationConfig } from "@/lib/react-query";
import getUserQueryOptions from "../user/useUser";
import { isValidABN } from "@/util/isValidABN";
import { z } from "zod";
import {
  addressSchema,
  phoneNumbersSchema,
} from "../user/useUpdateUserProfile";

export const organisationSchema = z.object({
  type: z.nativeEnum(OrganisationType).optional(),
  name: z.string().min(1, { message: "Company name is required" }).max(200, { message: "Company name must be no more than 200 characters" }).trim(),
  abn: z
    .string()
    .regex(/^\d{11}$/, { message: "Please enter a valid 11-digit ABN." })
    .refine(isValidABN, {
      message: "Invalid ABN. Please check that you've entered it correctly.",
    }),
  phoneNumbers: z.array(phoneNumbersSchema).optional(),
  addresses: z.array(addressSchema).optional(),
  uuid: z.string().optional(),
});

async function createOrganisation(organisation: Organisation) {
  const response = await DefaultService.createOrganisation(organisation);
  return response;
}

type UseCreateOrganisationOptions = {
  mutationConfig?: MutationConfig<typeof createOrganisation>;
};

export function useCreateOrganisation({
  mutationConfig,
}: UseCreateOrganisationOptions = {}) {
  const queryClient = useQueryClient();

  const { onSuccess, onError, ...restConfig } = mutationConfig || {};

  return useMutation({
    onSuccess: (data, ...args) => {
      queryClient.invalidateQueries({
        queryKey: getUserQueryOptions().queryKey,
      });
      onSuccess?.(data, ...args);
    },
    onError: (error, ...args) => {
      console.error("Error creating organisation:", error);
      onError?.(error, ...args);
    },
    ...restConfig,
    mutationFn: createOrganisation,
  });
}
